import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Button, Col, Card, CardBody } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_PAGINATION_LENGTH,
  INQUIRY_STATUSES,
} from '../../../config/app.config';
import { formatDate } from '../../../helpers/formatDate';

import PaginationComponent from '../../../shared/components/pagination/Pagination';
import HeadingText from '../../../shared/components/form/HeadingText';
import StandardTableToolbar from '../../../shared/components/table-toolbar/StandardTableToolbar';
import withRouter from '../../../shared/components/withRouter';

import {
  getAllInquiries,
  changeInquiryStatus,
} from '../../../redux/actions/inquiryActions';
import { toggleLoading } from '../../../redux/actions/commonActions';

import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import { useGetTopics } from '../../App/customize/hooks/useGetTopics';

import { InquiriesTable } from './Components';
import { useGetInquiriesHeaders } from './utils';
import { InquiryModalProvider } from './utils/InquiryModalContext';
import { InquiryModal } from './RequestDetails';
import {
  INQUIRY_DATE_SHORTCUTS,
  INQUIRY_FILTER_SEARCH_THRESHOLD,
  INQUIRY_SCOPE_OPTIONS,
  INQUIRY_SORT_OPTIONS,
  getInquiryFilterCategories,
  getProfileInquirySortOptions,
} from './utils/inquiryToolbarConfig';

const initialParams = {
  search: '',
  timeFilter: null,
  sortOrder: -1,
  sortField: 'createdAt',
  offset: 0,
  limit: DEFAULT_PAGINATION_LENGTH,
  filters: {},
  range: {},
  scope: 'open',
};

function reducer(params, action) {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...params, search: action.payload, offset: 0 };
    case 'SET_TIME_FILTER':
      return {
        ...params,
        timeFilter: action.payload,
        offset: 0,
      };
    case 'SET_SORT': {
      const { field, order } = action.payload;
      return {
        ...params,
        sortField: field ?? params.sortField,
        sortOrder: order ?? params.sortOrder,
        offset: 0,
      };
    }
    case 'SET_FILTER':
      return {
        ...params,
        filters: {
          ...params.filters,
          [action.payload.type]: action.payload.filters,
        },
        offset: 0,
      };
    case 'SET_SCOPE':
      return {
        ...params,
        scope: action.payload,
        offset: 0,
      };
    case 'CLEAR_ALL_FILTERS':
      return { ...params, filters: {}, offset: 0 };
    case 'SET_RANGE':
      return { ...params, range: action.payload, offset: 0 };
    case 'SET_LIMIT':
      return { ...params, limit: action.payload, offset: 0 };
    case 'SET_OFFSET':
      return { ...params, offset: action.payload };
    default:
      return params;
  }
}

function Inquiries({ parentId }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const petId = searchParams.get('pet');
  const isClientPage = !!parentId;

  const inquiriesHeaders = useGetInquiriesHeaders({ isClientPage });
  const { data: services } = useGetFacilityServices();
  const { data: topics = [] } = useGetTopics({ enabled: !isClientPage });
  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );

  const facilityId = useSelector((state) => state.user?.profile?.facility?._id);
  const { inquiriesList } = useSelector((state) => state.inquiries);

  const [params, localDispatch] = useReducer(reducer, initialParams);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [showUpdatedHint, setShowUpdatedHint] = useState(false);
  const updatedHintTimerRef = useRef(null);

const filterCategories = useMemo(
  () =>
    getInquiryFilterCategories({
      topics,
      doctors,
      includeDoctor: !services?.isBoardingOnly,
    }),
  [topics, doctors, services],
);

const profileSortOptions = useMemo(
  () => getProfileInquirySortOptions({ petId }),
  [petId],
);

useEffect(() => {
  if (isClientPage && petId && params.sortField === 'petName') {
    localDispatch({
      type: 'SET_SORT',
      payload: { field: 'createdAt', order: -1 },
    });
  }
}, [petId, isClientPage, params.sortField]);

const getInquiries = useCallback(async () => {
    const formattedStartDate = formatDate(params.range.startDate);
    const formattedEndDate = formatDate(params.range.endDate);

    const { doctorId, topicTitle, inquiryStatus } = params.filters;

    const resolvedInquiryStatus = isClientPage
      ? inquiryStatus?.length > 0
        ? inquiryStatus
        : null
      : params.scope === 'open'
        ? [INQUIRY_STATUSES.OPEN]
        : null;

    await dispatch(
      getAllInquiries({
        facilityId,
        params: {
          sort: {
            field: params.sortField,
            order: +params.sortOrder,
          },
          search: params.search,
          doctorId: doctorId?.length > 0 ? doctorId : null,
          parentId,
          petId,
          topicTitle: topicTitle?.length > 0 ? topicTitle : null,
          inquiryStatus: resolvedInquiryStatus,
          timeFilter: {
            type: params.timeFilter,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          },
          limit: params.limit,
          offset: params.offset,
        },
      }),
    );
  }, [facilityId, parentId, petId, params, dispatch, isClientPage]);

  useEffect(() => {
    const fetchInquiries = async () => {
      if (facilityId) {
        dispatch(toggleLoading(true));
        await getInquiries();
        dispatch(toggleLoading(false));
      }
    };

    fetchInquiries();
  }, [facilityId, getInquiries, dispatch]);

  useEffect(() => {
    setRows(inquiriesList?.data || []);
    setTotal(inquiriesList?.total || 0);
  }, [inquiriesList]);

  useEffect(() => {
    return () => {
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }
    };
  }, []);

  const handleTimeFilterChange = (timeFilter) => {
    localDispatch({ type: 'SET_TIME_FILTER', payload: timeFilter });
  };

  const handleSort = ({ field, order }) => {
    localDispatch({ type: 'SET_SORT', payload: { field, order } });
  };

  const handleFilter = (type, filters) => {
    localDispatch({ type: 'SET_FILTER', payload: { type, filters } });
  };

  const handleScopeChange = (scope) => {
    localDispatch({ type: 'SET_SCOPE', payload: scope });
  };

  const handleClearAllFilters = () => {
    localDispatch({ type: 'CLEAR_ALL_FILTERS' });
  };

  const handlePageLimit = (newLimit) => {
    localDispatch({ type: 'SET_LIMIT', payload: newLimit });
  };

  const handleSearchChange = (search) => {
    localDispatch({ type: 'SET_SEARCH', payload: search });
  };

  const handleRefresh = async () => {
    dispatch(toggleLoading(true));
    await getInquiries();
    dispatch(toggleLoading(false));

    if (!isClientPage) {
      setShowUpdatedHint(true);
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }
      updatedHintTimerRef.current = setTimeout(() => {
        updatedHintTimerRef.current = null;
        setShowUpdatedHint(false);
      }, 1500);
    }
  };

  const handleInquiryStatusChange = async (contactRequestId, newStatus) => {
    dispatch(toggleLoading(true));
    await dispatch(changeInquiryStatus(contactRequestId, newStatus));
    await getInquiries();
    dispatch(toggleLoading(false));
  };

  const hasAdditionalNarrowing =
    !!params.search?.trim() ||
    !!params.timeFilter ||
    Object.values(params.filters || {}).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
  const emptyStateMessage = isClientPage
    ? 'No Inquiries to display'
    : hasAdditionalNarrowing
      ? 'No matching Inquiries to display'
      : 'No Inquiries to display';

  const content = (

    <>
{!isClientPage ? (
  <div className="sticky-table">
    <div className="ptb-board-header">
      <div className="page-title-with-action ptb-board-header__title-row">
        <HeadingText text="Inquiries" />
        <Button
          type="button"
          className="btn-utility"
          onClick={handleRefresh}
        >
          Refresh
        </Button>
        {showUpdatedHint ? (
          <span style={{ fontSize: '12px', color: '#999' }}>
            Updated!
          </span>
        ) : null}
      </div>
      <div className="ptb-board-header__subtitle-row">
        <div className="ptb-board-header__subtitle-stack">
          <div className="ptb-board-header__subtitle">
            Inquiries submitted by Pet Parents
          </div>
          <div className="ptb-board-header__subtitle">
            through the Loyal Pet mobile app
          </div>
        </div>
      </div>
    </div>
    <div className="refill-requests-toolbar">
      <StandardTableToolbar
        searchValue={params.search}
        onSearchChange={handleSearchChange}
        scope={params.scope}
        onScopeChange={handleScopeChange}
        scopeOptions={INQUIRY_SCOPE_OPTIONS}
        sortField={params.sortField}
        sortOrder={params.sortOrder}
        onSortChange={handleSort}
        sortOptions={INQUIRY_SORT_OPTIONS}
        filters={params.filters}
        onFilterChange={handleFilter}
        onClearAllFilters={handleClearAllFilters}
        filterCategories={filterCategories}
        filterSearchThreshold={INQUIRY_FILTER_SEARCH_THRESHOLD}
        timeFilter={params.timeFilter}
        onTimeFilterChange={handleTimeFilterChange}
        dateShortcuts={INQUIRY_DATE_SHORTCUTS}
      />
      </div>
  </div>
) : (
  <div className="refill-requests-toolbar inquiries-toolbar--profile">
    <StandardTableToolbar
      sortField={params.sortField}
      sortOrder={params.sortOrder}
      onSortChange={handleSort}
      sortOptions={profileSortOptions}
    />
  </div>
)}
      <InquiriesTable
        stickyTop="60px"
        heads={inquiriesHeaders}
        rows={rows}
        sortField={params.sortField}
        sortOrder={params.sortOrder}
        handleSort={handleSort}
        handleFilter={handleFilter}
        onStatusChange={handleInquiryStatusChange}
        emptyStateMessage={emptyStateMessage}
      />
      <PaginationComponent
        handlePageLimit={handlePageLimit}
        nextPage={() =>
          localDispatch({
            type: 'SET_OFFSET',
            payload: params.offset + params.limit,
          })
        }
        prevPage={() =>
          localDispatch({
            type: 'SET_OFFSET',
            payload: params.offset - params.limit,
          })
        }
        gotoPage={(page) =>
          localDispatch({
            type: 'SET_OFFSET',
            payload: params.limit * page,
          })
        }
        limit={params.limit}
        offset={params.offset}
        total={total}
      />
    </>
  );

  return (
    <InquiryModalProvider handleRefetchInquiries={getInquiries}>
      {isClientPage ? (
      <div className="inquiries-container inquiries-container--profile">
        {content}
      </div>
    ) : (
        <Col md={12} lg={12} className="manage-patient-container admin-container">
          <Card>
            <CardBody className="inquiries-container">
              {content}
            </CardBody>
          </Card>
        </Col>
      )}
      <InquiryModal />
    </InquiryModalProvider>
  );
}

export default withRouter(Inquiries);
